export interface TourStepPlan {
  id: string;
  title: string;
  body: string;
  route?: string;
  target: string;
}

/** One tour that follows the current setup path. Targets are real elements. */
export const WELCOME_TOUR_STEPS: TourStepPlan[] = [
  {
    id: "welcome",
    title: "Welcome to Speak",
    body: "This short tour follows the real setup: an agent, a phone number, your calendar, a test call, a campaign, and billing. You can close it and pick it up later.",
    route: "/dashboard",
    target: "body",
  },
  {
    id: "dashboard",
    title: "Your dashboard",
    body: "Campaigns, leads, and calls show up here once you start using the account.",
    route: "/dashboard",
    target: ".metric-cards",
  },
  {
    id: "navigation",
    title: "Move around",
    body: "The sidebar is how you reach agents, leads, campaigns, the calendar, and settings.",
    route: "/dashboard",
    target: ".sidebar-nav",
  },
  {
    id: "create-agent",
    title: "Create an agent",
    body: "Start here. Give the agent a name, a voice, and a prompt. You can test it as soon as it is saved.",
    route: "/agents",
    target: "[data-tour='create-agent-btn']",
  },
  {
    id: "phone-number",
    title: "Add a phone number",
    body: "Buy or assign a Telnyx number in Settings. That number is the caller ID for outbound calls and the number people dial to reach the agent.",
    route: "/settings?tab=phone-numbers",
    target: "[data-tour='phone-numbers-tab']",
  },
  {
    id: "calendar",
    title: "Connect a calendar",
    body: "Connect Google, Outlook, or Cal.com so the agent can offer times that are actually free. Calendar tools are on the Pro plan.",
    route: "/calendar",
    target: ".calendar-header",
  },
  {
    id: "test-call",
    title: "Place a test call",
    body: "Open an agent and choose Test. Call your own mobile. The call is blocked if that number is on your do-not-call list.",
    route: "/agents",
    target: "[data-tour='agents-page']",
  },
  {
    id: "campaigns",
    title: "Run a campaign",
    body: "When the test call sounds right, create a campaign and attach leads. Campaigns are a Pro feature.",
    route: "/campaigns",
    target: "[data-tour='create-campaign-btn']",
  },
  {
    id: "billing",
    title: "Billing",
    body: "Balance, plan, and invoices live on the billing page. You can also open it from the account menu.",
    route: "/billing",
    target: "[data-tour='billing-page']",
  },
  {
    id: "done",
    title: "You're ready",
    body: "That's the setup. This tour will stay closed. You can run it again from the account menu.",
    route: "/dashboard",
    target: "body",
  },
];

export interface TourProgress {
  active: boolean;
  step: number;
  completed: boolean;
  dismissed: boolean;
}

export const EMPTY_TOUR: TourProgress = {
  active: false,
  step: 0,
  completed: false,
  dismissed: false,
};

export function clampTourStep(step: number, steps: TourStepPlan[] = WELCOME_TOUR_STEPS): number {
  if (!Number.isFinite(step)) return 0;
  return Math.min(steps.length - 1, Math.max(0, Math.floor(step)));
}

export function targetIsPresent(target: string, isPresent: (selector: string) => boolean): boolean {
  if (!target || target === "body") return true;
  return isPresent(target);
}

/**
 * Move one step, then skip any step whose target is missing.
 * Stops at the ends. Never walks in a circle.
 */
export function moveTourIndex(
  index: number,
  direction: 1 | -1,
  isPresent: (selector: string) => boolean,
  steps: TourStepPlan[] = WELCOME_TOUR_STEPS,
): number {
  const start = clampTourStep(index, steps);
  let next = start + direction;
  let hops = 0;
  while (next >= 0 && next < steps.length && hops < steps.length) {
    if (targetIsPresent(steps[next].target, isPresent)) return next;
    next += direction;
    hops += 1;
  }
  return start;
}

export function dismissTour(state: TourProgress): TourProgress {
  return { ...state, active: false, dismissed: true, completed: state.completed };
}

export function completeTour(state: TourProgress): TourProgress {
  return { ...state, active: false, completed: true, dismissed: true, step: WELCOME_TOUR_STEPS.length - 1 };
}

export function resumeTour(state: TourProgress): TourProgress {
  if (state.completed) return state;
  return { ...state, active: true, dismissed: false, step: clampTourStep(state.step) };
}

export function restartTour(): TourProgress {
  return { active: true, step: 0, completed: false, dismissed: false };
}

export function routePath(route: string | undefined): string | null {
  if (!route) return null;
  return route.split("?")[0] || null;
}
